// services/icd11Names.ts — SKIN-612
import axios from 'axios';

/**
 * SKIN-612 — WHO ICD-11 names from the API, for the codes no label the visit stores names.
 *
 * The web holds no catalogue; the API does (sherwood-api docs/features/icd11-names). The
 * request carries codes only — never other record data — and each code is asked once per page
 * load: an answer, named or not, is kept for the rest of it (the catalogue is a fixed WHO
 * release). A failed request keeps nothing, so the codes stay bare and are asked again later.
 */
const BASE_URL = import.meta.env.VITE_APP_API_URL + '/researcher/investigation';

/** The API's own limit per request (MAX_ICD11_CODES_PER_REQUEST). */
const MAX_CODES_PER_REQUEST = 200;

/** What the API accepts as a code (icd11NameRead.ts); anything else is never sent. */
const CODE_PATTERN = /^[A-Za-z0-9._&-]{1,32}$/;

/** code -> WHO name, or null when the catalogue does not hold it. */
const answered = new Map<string, string | null>();
/** code -> the request that will answer it. */
const pending = new Map<string, Promise<void>>();

/** The names already known for `codes`. */
export function cachedIcd11Names(codes: ReadonlyArray<string>): Map<string, string> {
    const names = new Map<string, string>();
    codes.forEach(code => {
        const name = answered.get(code);
        if (name) names.set(code, name);
    });
    return names;
}

async function request(uuidInvestigation: string, codes: string[]): Promise<void> {
    try {
        const response = await axios.get(`${BASE_URL}/${uuidInvestigation}/icd11/names`, {
            headers: { Authorization: localStorage.getItem('jwt') ?? '' },
            params: { codes: codes.join(',') },
        });
        const names = response?.data?.names;
        if (!names || typeof names !== 'object') return;
        codes.forEach(code => answered.set(code, typeof names[code] === 'string' ? names[code] : null));
    } catch {
        // The codes stay bare: a name is a convenience, never a reason to fail a page.
    } finally {
        codes.forEach(code => pending.delete(code));
    }
}

/** The WHO names of `codes`, asking the API only for those not yet answered. */
export async function fetchIcd11Names(uuidInvestigation: string, codes: ReadonlyArray<string>): Promise<Map<string, string>> {
    const valid = Array.from(new Set(codes)).filter(code => CODE_PATTERN.test(code));
    const toAsk = valid.filter(code => !answered.has(code) && !pending.has(code));
    for (let i = 0; i < toAsk.length; i += MAX_CODES_PER_REQUEST) {
        const chunk = toAsk.slice(i, i + MAX_CODES_PER_REQUEST);
        const answer = request(uuidInvestigation, chunk);
        chunk.forEach(code => pending.set(code, answer));
    }
    await Promise.all(valid.map(code => pending.get(code)).filter(Boolean));
    return cachedIcd11Names(valid);
}
